import AdvanceTable from 'components/base/AdvanceTable';
import Badge from 'components/base/Badge';
import {
  latestReviewsTableData,
  LatestReviewsTableDataType
} from 'data/LatestReviewsTableData';
import useAdvanceTable from 'hooks/useAdvanceTable';
import AdvanceTableProvider from 'providers/AdvanceTableProvider';
import { Link } from 'react-router-dom';
import FeatherIcon from 'feather-icons-react';
import AdvanceTableFooter from 'components/base/AdvanceTableFooter';
import { ColumnDef } from '@tanstack/react-table';
import Rating from 'components/base/Rating';
import RevealDropdown, {
  RevealDropdownTrigger
} from 'components/base/RevealDropdown';
import ActionDropdownItems from 'components/common/ActionDropdownItems';
import { ProductReview, ProductReviewDetail } from 'api/products/_requests';
import { useQuery } from '@tanstack/react-query';
import team15 from 'assets/img/team/avatar.webp';

const columns: ColumnDef<ProductReviewDetail>[] = [
  {
    accessorKey: 'productId',
    header: () => 'Product',
    cell: ({ row: { original } }) => {
      const { productId } = original;
      return (
        <Link
          to={`/product-details/${productId?._id}`}
          className="fw-semibold line-clamp-1"
        >
          {`${productId?.name.slice(0, 46)}${
            productId?.name.length > 46 ? '...' : ''
          }`}
        </Link>
      );
    },
    meta: {
      headerProps: { style: { minWidth: 200, maxWidth: 250 } },
      cellProps: { className: 'pe-3' }
    }
  },
  {
    accessorKey: 'userId',
    header: 'Customer',
    cell: ({ row: { original } }) => {
      const user = original.userId;
      return (
        <div className="d-flex align-items-center gap-2">
          <img
            src={user?.avatar || team15}
            alt={user?.fullName}
            className="rounded-circle"
            style={{ width: 24, height: 24 }}
          />
          <span>{user?.fullName || ''}</span>
        </div>
      );
    },
    meta: {
      headerProps: { style: { minWidth: 150, maxWidth: 180 } }
    }
  },
  {
    accessorKey: 'rating',
    header: 'Rating',
    cell: ({ row: { original } }) => {
      const { rating } = original;
      return <Rating readonly initialValue={rating} iconClass="fs-10" />;
    },
    meta: {
      headerProps: { style: { width: 100, textAlign: 'center' } },
      cellProps: { className: 'text-center' }
    }
  },
  {
    accessorKey: 'review',
    header: 'Review',
    cell: ({ row: { original } }) => {
      const { review } = original;
      return (
        <p className="fs--1 fw-semibold text-body-highlight mb-0 line-clamp-3">
          {review.slice(0, 134)}
          {review.length > 134 && (
            <>
              {'...'}
              <Link to="#!">See more</Link>
            </>
          )}
        </p>
      );
    },
    meta: {
      headerProps: { style: { minWidth: 300, maxWidth: 400 } },
      cellProps: { className: 'pe-7' }
    }
  },
  {
    accessorKey: 'createdAt',
    header: 'Date',
    cell: ({ row }) => {
      const date = new Date(row?.original?.createdAt);
      return date.toLocaleDateString(); // Format: MM/DD/YYYY or locale
    },
    meta: {
      headerProps: {
        className: 'text-end',
        style: { width: 120 }
      },
      cellProps: {
        className: 'text-end text-body-tertiary white-space-nowrap'
      }
    }
  }
];

const EcomVendorBrandProductReviewsTable = ({
  vendorbrandproductReviews
}: {
  vendorbrandproductReviews?: ProductReviewDetail[];
}) => {
  const table = useAdvanceTable({
    data: vendorbrandproductReviews ?? [],
    columns,
    pageSize: 6,
    pagination: true,
    sortable: true
  });

  return (
    <>
      <AdvanceTableProvider {...table}>
        <div className="border-y border-translucent">
          <AdvanceTable tableProps={{ className: 'phoenix-table fs-9' }} />
          <AdvanceTableFooter pagination />
        </div>
      </AdvanceTableProvider>
    </>
  );
};

export default EcomVendorBrandProductReviewsTable;
